import { FormatNumberOptions } from '@formatjs/intl';
import Highcharts, { GradientColorObject, PatternObject } from 'highcharts';

type LineGraphOptions = {
  xAxisTitle: string | React.ReactNode;
  yAxisTitle: string | React.ReactNode;
  xAxisType?: Highcharts.AxisTypeValue;
  formatNumber(value: Parameters<Intl.NumberFormat['format']>[0], opts?: FormatNumberOptions): string;
};

type Point = Highcharts.Point & {
  strokeColor: string | GradientColorObject | PatternObject | undefined;
};

const commonStyle = {
  fontSize: '12px',
  color: `#1A2A3A`,
  fontFamily: 'DM Sans, Arial',
};

const getLineGraphOptions = ({ xAxisTitle, yAxisTitle, xAxisType = 'linear', formatNumber }: LineGraphOptions): Highcharts.Options => {
  const options: Highcharts.Options = {
    chart: {
      zoomType: 'x',
      marginTop: 40,
      style: {
        fontFamily: 'DM Sans, Arial',
      },
    },
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    plotOptions: {
      series: {
        marker: {
          lineWidth: 1,
          height: 0,
          width: 0,
          radius: 0,
        },
      },
    },
    tooltip: {
      shared: true,
      headerFormat: `<div style="padding:5px 0; font-weight: 900;">
          <span>{point.key}</span>
     </div>
      `,
      backgroundColor: `#FFFFFF`,
      valueDecimals: 2,
      style: {
        fontSize: '12px',
        zIndex: 20000,
      },
      useHTML: true,
      pointFormatter: function () {
        let point = this as Point;

        point.strokeColor = point.color === `#FFFFFF` ? `#1A2A3A` : point.color;

        return `<div style="position: relative; display: flex;">
          <span style="position: absolute; padding: 5px 0;">
            <svg height="16" width="16" viewBox="0 0 12 12" >
              <circle cx="7" cy="7" r="6" stroke=${point.strokeColor} fill=${point.color} />
            </svg>
          </span>
          <span style=" padding: 5px 20px;">${this.series.name}</span>
          <span style=" padding: 5px 0; margin-left: auto; font-weight: 900;">
          ${
            !!this.options.low && !!this.options.high
              ? `${formatNumber(this.options.low)}</b> - <b>${formatNumber(this.options.high)}`
              : `${!!(this as any).label ? (this as any).label : formatNumber(this.y ?? 0)}`
          }
          </span>
          </div>
          `;
      },
    },
    xAxis: [xAxis(xAxisTitle, xAxisType)],
    yAxis: [yAxis(yAxisTitle, false)],
  } as Highcharts.Options;
  return options;
};

export const xAxis = (text: string | React.ReactNode, type: string): Highcharts.XAxisOptions => {
  return {
    type: type,
    lineWidth: 1,
    lineColor: '#cbe6de',
    crosshair: {
      color: '#979797',
      dashStyle: 'Dot',
    },
    labels: {
      style: {
        ...commonStyle,
      },
    },
    title: {
      text: `${text}`,
      align: 'low',
      x: 0,
      style: {
        ...commonStyle,
        fontWeight: '700',
        letterSpacing: '1px',
      },
    },
  } as Highcharts.XAxisOptions;
};

export const yAxis = (text: string | React.ReactNode, opposite: boolean): Highcharts.XAxisOptions => {
  return {
    gridLineWidth: 1,
    gridLineColor: '#cbe6de',
    crosshair: false,
    labels: {
      format: '{value}',
      x: -15,
      y: -3,
      align: 'left',
      style: {
        ...commonStyle,
      },
    },
    opposite: opposite,
    title: {
      text: `${text}`,
      x: -10,
      y: 0,
      style: {
        whiteSpace: 'nowrap',
        width: 15,
        ...commonStyle,
        fontWeight: '700',
        letterSpacing: '1px',
      },
    },
  } as Highcharts.XAxisOptions;
};

export default getLineGraphOptions;
